import { Path } from '@cloudio-saas/statex';
import {
  BOOLEAN_OPS,
  DATE_OPS,
  Filters,
  MULTIPLE_DATE_OPS,
  MULTIPLE_NUMBER_OPS,
  MULTIPLE_SELECT_OPS,
  MULTIPLE_STRING_OPS,
  NUMBER_OPS,
  SELECT_OPS,
  STRING_OPS,
  YN_TF_OPS,
} from '@cloudio-saas/datasource-types';
import { Combiner } from 'components/SmartSearch/SearchUtils';
import { Operator } from 'componentregistry/components/SmartSearch';

export interface EntryProps {
  expandFilters: boolean;
  parentPath: Path;
  index: number;
  getOptions?: (
    inputName: string,
    withOperator?: boolean,
  ) => { value: string; label: string }[];
  depth: number;
  entry: ClientFilter;
  onUpdateName?: (path: Path, _name: string) => void;
  onUpdateValue?: (path: Path, value: ValueType) => void;
  onUpdateOperator?: (path: Path, operator: Operator) => void;
  onRemoveEntry?: (path: Path) => void;
  onEditEntry?: (path: Path) => void;
  onAddEntry?: (path: Path) => void;
  onHandleEditedEntry?: (removeIncomplete?: boolean) => void;
  appUid: string;
  pageId: string;
  itemId: string;
  editMode: boolean;
}

export const nestedOptions = [
  {
    value: 'allOf',
    label: 'All Of',
  },
  {
    value: 'anyOf',
    label: 'Any Of',
  },
  {
    value: 'noneOf',
    label: 'None Of',
  },
];

export const combinerOptions = {
  allOf: 'All Of',
  anyOf: 'Any Of',
  noneOf: 'None Of',
};

export const multipleOperators = [
  'in',
  'nin',
  'hasAll',
  'hasAny',
  'notAny',
  'iHasAll',
  'iHasAny',
  'iNotAny',
  'iIn',
  'iNin',
  'bn',
];

export interface ClientBaseFilter {
  key: string;
  name: string;
  label: string;
  editing?: boolean;
  excludeOperators?: string;
}

export interface ClientBooleanFilter extends ClientBaseFilter {
  operator?: keyof typeof BOOLEAN_OPS;
  dataType: 'Boolean';
  value?: boolean;
}

export interface ClientYNFilter extends ClientBaseFilter {
  operator?: keyof typeof YN_TF_OPS;
  dataType: 'YN';
  value?: string;
}

export interface ClientTFFilter extends ClientBaseFilter {
  operator?: keyof typeof YN_TF_OPS;
  dataType: 'TF';
  value?: string;
}

export interface ClientStringFilter extends ClientBaseFilter {
  operator?: keyof typeof STRING_OPS;
  dataType: 'Text';
  value?: string;
}

export interface ClientMultipleStringFilter extends ClientBaseFilter {
  operator?: keyof typeof MULTIPLE_STRING_OPS;
  dataType: 'Text';
  value?: string[];
}

export interface ClientSelectFilter extends ClientBaseFilter {
  operator?: keyof typeof SELECT_OPS;
  value?: string;
  dataType: 'Select' | 'Lookup';
  baseDataType: 'Number' | 'Text';
}

export interface ClientMultipleSelectFilter extends ClientBaseFilter {
  operator?: keyof typeof MULTIPLE_SELECT_OPS;
  value?: string[];
  dataType: 'Select' | 'Lookup';
  baseDataType: 'Number' | 'Text';
}

export interface ClientDateFilter extends ClientBaseFilter {
  operator?: keyof typeof DATE_OPS;
  value?: Date;
  dataType: 'Date';
  baseDataType: 'StringDateTime' | 'DateTime' | 'Date';
  includesTime?: boolean;
}

export interface ClientMultipleDateFilter extends ClientBaseFilter {
  operator?: keyof typeof MULTIPLE_DATE_OPS;
  value?: Date[];
  dataType: 'Date';
  baseDataType: 'StringDateTime' | 'DateTime' | 'Date';
  includesTime?: boolean;
}

export interface ClientNumberFilter extends ClientBaseFilter {
  operator?: keyof typeof NUMBER_OPS;
  value?: number;
  dataType: 'Number';
}

export interface ClientMultipleNumberFilter extends ClientBaseFilter {
  operator?: keyof typeof MULTIPLE_NUMBER_OPS;
  value?: number[];
  dataType: 'Number';
}

export interface ClientNestedFilter extends ClientBaseFilter {
  name: Combiner;
  filters: Filters<unknown>;
}
export type ClientNonNestedFilter =
  | ClientBooleanFilter
  | ClientStringFilter
  | ClientMultipleStringFilter
  | ClientDateFilter
  | ClientMultipleDateFilter
  | ClientNumberFilter
  | ClientMultipleNumberFilter
  | ClientSelectFilter
  | ClientMultipleSelectFilter
  | ClientYNFilter
  | ClientTFFilter;

export type ClientFilter = ClientNonNestedFilter | ClientNestedFilter;

export type ValueType =
  | boolean
  | string
  | number
  | Date
  | string[]
  | (number | undefined | null)[]
  | (Date | null | undefined)[]
  | undefined
  | null;

export type DateValueType = string | Date | null;
export type MultipleDateValueType = DateValueType[];
